// based on https://github.com/ngx-translate/core/issues/223#issuecomment-659418206

/**
 * There are some cases in the app where click on an anchor tag should open modal window. Since this behaviour is different from browser’s default one,
 * we can not just use the replacement mechanism provided by @ngx/translate. The purpose of this directive is to add that mechanism.
 * This is done by using the two directives together.
 * The first one is `idealsTranslatedContent`, where you pass translation key with link to replace. This link must contain an href attribute with placeholder
 * in curly braces and the translated text inside anchor tag that should be wrapped.
 * The second one is `*idealsTranslatedWrapper` structural directive, where you have to pass a placeholder from the href attribute and put the same placeholder
 * inside its content.
 * As a result the anchor tag will be replaced by the element, to which this directive is applied, with the content that is inside of anchor tag in the
 * original translation.
 * Example of usage:
 * in en-US.json:
 * "common.INFO.contact_to_verify_identity": "Contact <a href=\"{{iDealsSupportTeam}}\">Customer Support Team</a> to verify your identity"
 * in template.html:
 * <p idealsTranslatedContent="common.INFO.contact_to_verify_identity">
 *   <ideals-help-button *idealsTranslatedWrapper="'iDealsSupportTeam'">iDealsSupportTeam</ideals-help-button>&nbsp;
 * </p>
 * resulted.html
 * <p idealsTranslatedContent="common.INFO.contact_to_verify_identity">
 *   Contact
 *   <ideals-help-button   *idealsTranslatedElement="'iDealsSupportTeam'">Customer Support Team</ideals-help-button>
 *   to verify your identity
 * </p>
 */

import { contentChildren, Directive, effect, ElementRef, inject, Injector, input, OnInit, Renderer2 } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { TranslatedWrapperDirective } from './translated-wrapper.directive';

const TOKEN_START_DEMARC = '<a href=';
const TOKEN_END_DEMARC = '</a>';
const TOKEN_CLOSE = '>';
const ELEMENT_KEY_START_DEMARC = '{{';
const ELEMENT_KEY_END_DEMARC = '}}';

@Directive({
  selector: '[idealsTranslatedContent]',
})
export class TranslatedContentDirective implements OnInit {
  readonly translationKey = input.required<string>({ alias: 'idealsTranslatedContent' });

  protected readonly elements = contentChildren(TranslatedWrapperDirective);

  readonly #element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  readonly #injector = inject(Injector);
  readonly #renderer = inject(Renderer2);
  readonly #translateService = inject(TranslateService);

  readonly #rawTranslation = toSignal(
    toObservable(this.translationKey).pipe(
      switchMap((key) => merge(
        this.#translateService.get(key),
        this.#translateService.onLangChange.pipe(switchMap(() => this.#translateService.get(key))),
      )),
      map((translation: string) => translation),
    ),
  );

  ngOnInit(): void {
    this.#effects();
  }

  #effects(): void {
    effect(() => {
      const rawTranslation = this.#rawTranslation();

      if (rawTranslation === undefined) {
        return;
      }

      this.#render(rawTranslation, this.elements());
    }, { injector: this.#injector });
  }

  #render(rawTranslation: string, elements: readonly TranslatedWrapperDirective[]): void {
    if (!rawTranslation) {
      throw new Error(`No resource matching the key '${this.translationKey()}'`);
    }

    while (this.#element.firstChild) {
      // do not use renderer.removeChild() here because it does not remove all children.
      // possibly this is an angular bug. try to use renderer after upgrading on latest versions
      this.#element.firstChild.parentNode.removeChild(this.#element.firstChild);
    }

    let lastTokenEnd = 0;

    while (lastTokenEnd < rawTranslation.length) {
      const tokenStartDemarc = rawTranslation.indexOf(TOKEN_START_DEMARC, lastTokenEnd);

      if (tokenStartDemarc < 0) {
        break;
      }

      const translatedTextStart = rawTranslation.indexOf(TOKEN_CLOSE, tokenStartDemarc) + TOKEN_CLOSE.length;
      const translatedTextEnd = rawTranslation.indexOf(TOKEN_END_DEMARC, translatedTextStart);
      const elementKeyStart = rawTranslation.indexOf(ELEMENT_KEY_START_DEMARC, tokenStartDemarc) + ELEMENT_KEY_START_DEMARC.length;
      const elementKeyEnd = rawTranslation.indexOf(ELEMENT_KEY_END_DEMARC, tokenStartDemarc);

      if (translatedTextEnd < 0) {
        throw new Error(`Encountered unterminated token in translation string '${this.translationKey()}'`);
      }

      const translatedText = rawTranslation.substring(translatedTextStart, translatedTextEnd);
      const tokenEndDemarc = translatedTextEnd + TOKEN_END_DEMARC.length;
      const precedingText = rawTranslation.substring(lastTokenEnd, tokenStartDemarc);

      const precedingTextElement = this.#renderer.createText(precedingText) as Text;

      this.#renderer.appendChild(this.#element, precedingTextElement);

      const elementKey = rawTranslation.substring(elementKeyStart, elementKeyEnd);
      const embeddedElementTemplate = elements.find((element) => element.elementKey() === elementKey);

      if (embeddedElementTemplate) {
        const embeddedElementView = embeddedElementTemplate.viewRef.createEmbeddedView(embeddedElementTemplate.templateRef);
        const rootElement = embeddedElementView.rootNodes[0] as Element;

        if (rootElement.children.length === 0) {
          this.#renderer.setProperty(rootElement, 'textContent', translatedText);
        } else {
          const allChildren = rootElement.querySelectorAll('*');

          // use for..of instead forEach to support IE
          for (const child of allChildren) {
            if (child.textContent.trim() === elementKey) {
              this.#renderer.setProperty(child, 'textContent', translatedText);
            }
          }
        }

        this.#renderer.appendChild(this.#element, rootElement);
      } else {
        const missingTokenText = rawTranslation.substring(tokenStartDemarc, tokenEndDemarc);
        const missingTokenElement = this.#renderer.createText(missingTokenText) as Text;

        this.#renderer.appendChild(this.#element, missingTokenElement);
      }

      lastTokenEnd = tokenEndDemarc;
    }

    const trailingText = rawTranslation.substring(lastTokenEnd);
    const trailingTextElement = this.#renderer.createText(trailingText) as Text;

    this.#renderer.appendChild(this.#element, trailingTextElement);
  }
}
